// src/pages/.../NewsPageResponsables.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  getNewsResponsables,
  deleteNews,
  createNews,
} from "../../services/newsServices";
import { useUser } from "../../contexts/UserContext";
import { getRoleFromRoleId } from "../../utils/roleHelper";
import useScrollToTop from "../../hooks/useScrollToTop";
import Button from "../../components/Button/Button";
import InfoBubble from "../../components/InfoBubble/InfoBubble";
import NewsCard from "./NewsResponsablesCard";
import CreateNewsModal from "./CreateNewsResponsablesModal";
import EditNewsModal from "./EditNewsResponsablesModal";
import { ToastContainer, toast } from "react-toastify";
import ScrollIndicator from "../../hooks/ScrollIndicator";
import PageHeader from "../../components/Layout/PageHeader";

const NewsPageResponsables = () => {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [newsList, setNewsList] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentNews, setCurrentNews] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const isAdminOrBureau =
    userRole === "Administrateur" || userRole === "Bureau";

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        const data = await getNewsResponsables();
        setNewsList(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Failed to fetch news", error);
        toast.error("Erreur lors du chargement des actualités");
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  const refreshNewsList = async () => {
    try {
      const updatedNewsList = await getNewsResponsables();
      setNewsList(Array.isArray(updatedNewsList) ? updatedNewsList : []);
      return updatedNewsList;
    } catch (error) {
      console.error("Failed to refresh news", error);
      throw error;
    }
  };

  const handleAddNewsClick = () => {
    setCurrentNews(null);
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleCreateSuccess = async (newsData) => {
    try {
      await createNews(newsData);
      closeCreateModal();
      await refreshNewsList();
      toast.success("Actualité créée avec succès");
    } catch (error) {
      console.error("Erreur lors de la création de l'actualité:", error);
      toast.error("Erreur lors de la création de l'actualité.");
    }
  };

  const handleEditNews = (news) => {
    if (isAdminOrBureau) {
      setCurrentNews(news);
      setIsEditModalOpen(true);
    }
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setCurrentNews(null);
  };

  const handleEditSuccess = async () => {
    closeEditModal();
    await refreshNewsList();
    toast.success("Actualité mise à jour avec succès");
  };

  const handleDeleteNews = async (id) => {
    if (!isAdminOrBureau) return;
    try {
      await deleteNews(id);
      setNewsList((prev) => prev.filter((news) => news.id !== id));
      toast.success("Actualité supprimée avec succès");
    } catch (error) {
      console.error("Failed to delete news", error);
      toast.error("Erreur lors de la suppression de l'actualité");
    }
  };

  const filteredNews = useMemo(() => {
    let list = [...newsList];

    // Admin/Bureau : peuvent choisir d'afficher les inactives ou non
    if (!isAdminOrBureau || !showInactive) {
      list = list.filter((n) => n.is_active);
    }

    // Tri : dernières actus en premier
    list.sort((a, b) => {
      const da = new Date(a.created_at);
      const db = new Date(b.created_at);
      const va = isNaN(da) ? 0 : da.getTime();
      const vb = isNaN(db) ? 0 : db.getTime();
      return vb - va;
    });

    // Recherche texte : titre + contenu + auteur
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((n) => {
        const title = (n.title || "").toLowerCase();
        const content = (n.content || "").toLowerCase();
        const authorName = `${n.author?.prenom || ""} ${
          n.author?.nom || ""
        }`.toLowerCase();
        return (
          title.includes(q) ||
          content.includes(q) ||
          (authorName && authorName.includes(q))
        );
      });
    }

    return list;
  }, [newsList, isAdminOrBureau, showInactive, searchQuery]);

  return (
    <div className="flex flex-col w-full mx-auto min-h-screen mt-[130px] sm:mt-[130px] md:mt-[150px] lg:mt-[150px] xl:mt-[150px] px-2 pb-10  dark:bg-slate-950 text-slate-900 dark:text-slate-50">
      <ScrollIndicator />

      {/* En-tête unifiée */}
      <PageHeader
        eyebrow="Responsables clubs"
        title="Actualités pour les clubs"
        subtitle="Retrouvez ici tous les messages importants envoyés par le secrétariat du comité départemental aux clubs et enseignants : informations administratives, convocations, rappels de dates, consignes, etc."
        align="center"
        size="md"
      />

      <div className="w-full max-w-6xl mx-auto space-y-4">
        <InfoBubble title="À propos des actualités clubs">
          <p>
            Cette page regroupe les e-mails et informations diffusés aux clubs
            et enseignants. Elle vous permet de retrouver facilement les
            messages importants sans fouiller dans votre boîte mail.
          </p>
        </InfoBubble>

        {/* Barre actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {isAdminOrBureau && (
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                text="Ajouter une actualité"
                onClick={handleAddNewsClick}
                className="w-full sm:w-auto"
              />
              <button
                type="button"
                onClick={() => setShowInactive((v) => !v)}
                className={`w-full sm:w-auto text-xs sm:text-sm inline-flex items-center justify-center rounded-full border px-3 py-1.5 ${
                  showInactive
                    ? "bg-slate-900 text-white border-slate-900"
                    : "border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200"
                }`}
              >
                {showInactive
                  ? "Afficher actives & inactives"
                  : "Afficher uniquement les actives"}
              </button>
            </div>
          )}

          <div className="flex-1 sm:flex-none">
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
              Rechercher dans les actualités
            </label>
            <input
              type="text"
              placeholder="Titre, contenu, auteur..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/70"
            />
          </div>
        </div>

        {/* Liste des actualités */}
        {loading ? (
          <div className="mt-6 text-center text-sm text-slate-500 dark:text-slate-300">
            Chargement des actualités…
          </div>
        ) : filteredNews.length === 0 ? (
          <div className="mt-6 text-center text-sm text-slate-500 dark:text-slate-300">
            Aucune actualité ne correspond à vos critères.
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredNews.map((news) => (
              <NewsCard
                key={news.id}
                news={news}
                canModify={isAdminOrBureau}
                onEdit={() => handleEditNews(news)}
                onDelete={() => handleDeleteNews(news.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modales */}
      <CreateNewsModal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        onCreateSuccess={handleCreateSuccess}
      />

      <EditNewsModal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        refreshList={refreshNewsList}
        news={currentNews}
        onEditSuccess={handleEditSuccess}
      />

      <ToastContainer />
    </div>
  );
};

export default NewsPageResponsables;
